import { useEffect, useState } from "react";

import { useDispatch } from "react-redux";

import FieldWrapper from "@/components/Form/components/FieldWrapper";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useUpdateFileMetadata } from "@/hooks/mutations/use-files";
import { setMessage } from "@/redux/notification";
import { formatNullableDateToLocal } from "@/utils/dateUtils";

import { DocSorterFileSchema } from "@api/generated/types.gen";

// Helper function to convert date string to HTML date input format (YYYY-MM-DD)
const formatDateForInput = (dateString: string | null | undefined): string => {
  if (!dateString) return "";

  // If it's already in YYYY-MM-DD format, return as is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }

  // If it's in DD-MM-YYYY format, convert to YYYY-MM-DD
  if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
    const [day, month, year] = dateString.split("-");
    return `${year}-${month}-${day}`;
  }

  // Try to parse as any valid date string and format
  try {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      const formatted = date.toISOString().split("T")[0];
      return formatted;
    }
  } catch (error) {
    console.error("Error parsing date:", error);
  }

  return "";
};

// Helper function to convert YYYY-MM-DD back to DD-MM-YYYY for display
const formatDateForDisplay = (dateString: string | null | undefined): string => {
  if (!dateString) return "";

  // If it's in YYYY-MM-DD format, convert to DD-MM-YYYY
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  }

  // If it's already in DD-MM-YYYY format, return as is
  if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
    return dateString;
  }

  // Try to parse as any valid date string and format to DD-MM-YYYY
  try {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    }
  } catch (error) {
    console.error("Error parsing date:", error);
  }

  return "";
};

type EditDocumentDateModalProps = {
  file: DocSorterFileSchema | null;
  isOpen: boolean;
  onClose: () => void;
};

export const EditDocumentDateModal = ({
  file,
  isOpen,
  onClose,
}: EditDocumentDateModalProps) => {
  const dispatch = useDispatch();
  const updateFileMetadata = useUpdateFileMetadata();
  const [date, setDate] = useState<string>(""); // Internal YYYY-MM-DD format for date input
  const [displayDate, setDisplayDate] = useState<string>(""); // DD-MM-YYYY format for display

  // Initialize form when file changes
  useEffect(() => {
    if (file) {
      const formattedDate = formatDateForInput(formatNullableDateToLocal(file.date));
      const displayFormattedDate = formatDateForDisplay(formattedDate);
      setDate(formattedDate);
      setDisplayDate(displayFormattedDate);
    }
  }, [file]);

  const handleSave = async () => {
    if (!file) return;

    try {
      await updateFileMetadata.mutateAsync({
        fileId: file.id,
        metadata: {
          date: date ? new Date(date).toISOString() : null,
        },
      });

      dispatch(
        setMessage({
          type: "success",
          content: "Document date updated successfully",
        })
      );
      onClose();
    } catch (_error) {
      dispatch(
        setMessage({
          type: "error",
          content: "Failed to update document date",
        })
      );
    }
  };

  const handleCancel = () => {
    // Reset form to original values
    if (file) {
      const formattedDate = formatDateForInput(formatNullableDateToLocal(file.date));
      const displayFormattedDate = formatDateForDisplay(formattedDate);
      setDate(formattedDate);
      setDisplayDate(displayFormattedDate);
    }
    onClose();
  };

  const handleDateChange = (value: string) => {
    setDate(value);
    setDisplayDate(formatDateForDisplay(value));
  };

  if (!file) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Document Date</DialogTitle>
        </DialogHeader>

        <div className="py-4">
          <FieldWrapper id="date" label="Date">
            <Input
              id="date"
              type="date"
              value={date}
              onChange={(e) => handleDateChange(e.target.value)}
              className="w-full"
            />
            {displayDate && (
              <p className="mt-2 text-sm text-gray-600">
                Display format: <span className="font-mono">{displayDate}</span>
              </p>
            )}
          </FieldWrapper>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="simple-outline"
            onClick={handleCancel}
            disabled={updateFileMetadata.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={updateFileMetadata.isPending}
          >
            {updateFileMetadata.isPending ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
